import { FormEvent, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage, blobErrorMessage } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import type { DocumentTemplate, GmailAccount, Rule, RuleRun, ScheduleSpec, TriggerType } from '../api/types';
import CronBuilder, { specToCron } from '../components/CronBuilder';
import PdfPreviewModal from '../components/PdfPreviewModal';
import PlaceholdersHelp from '../components/PlaceholdersHelp';

const DYNAMIC = new Set(['date', 'date_us', 'datetime', 'mes_ano', 'anomes', 'now', 'email_from', 'email_from_name', 'email_subject', 'email_body']);

type Draft = {
  id?: string;
  name: string;
  enabled: boolean;
  trigger_type: TriggerType;
  cron: string;
  schedule_spec: ScheduleSpec;
  watch_account_id: string;
  match_from: string;
  match_subject: string;
  from_account_id: string;
  to_addr: string;
  cc: string;
  subject_template: string;
  body_template: string;
  attachments: AttachmentDraft[];
  template_context: Record<string, string>;
  notify_on_error: boolean;
  notify_on_success: boolean;
  notify_on_match: boolean;
};

type AttachmentDraft = { template_id: string; filename: string; as_pdf: boolean };

function emptyDraft(): Draft {
  return {
    name: '',
    enabled: true,
    trigger_type: 'schedule',
    cron: '0 9 * * *',
    schedule_spec: { kind: 'daily', time: '09:00' },
    watch_account_id: '',
    match_from: '',
    match_subject: '',
    from_account_id: '',
    to_addr: '',
    cc: '',
    subject_template: '',
    body_template: '',
    attachments: [],
    template_context: {},
    notify_on_error: true,
    notify_on_success: false,
    notify_on_match: false,
  };
}

function ruleToDraft(r: Rule): Draft {
  return {
    id: r.id,
    name: r.name,
    enabled: r.enabled,
    trigger_type: r.trigger_type,
    cron: r.cron ?? '0 9 * * *',
    schedule_spec: r.schedule_spec ?? { kind: 'advanced' },
    watch_account_id: r.watch_account_id ?? '',
    match_from: r.match_from ?? '',
    match_subject: r.match_subject ?? '',
    from_account_id: r.from_account_id ?? '',
    to_addr: r.to_addr ?? '',
    cc: r.cc ?? '',
    subject_template: r.subject_template ?? '',
    body_template: r.body_template ?? '',
    attachments: (r.attachments ?? []).map((a) => ({
      template_id: a.template_id,
      filename: a.filename ?? '',
      as_pdf: a.as_pdf ?? false,
    })),
    template_context: r.template_context ?? {},
    notify_on_error: r.notify_on_error,
    notify_on_success: r.notify_on_success,
    notify_on_match: r.notify_on_match,
  };
}

export default function RulesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [runsFor, setRunsFor] = useState<string | null>(null);

  // Preview de anexo
  const [previewRule, setPreviewRule] = useState<Rule | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  function closePreview() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPreviewRule(null);
    setPdfUrl(null);
    setPdfError(null);
  }

  async function openPreview(r: Rule, index = 0) {
    setPreviewRule(r);
    setPdfUrl(null);
    setPdfError(null);
    setPdfLoading(true);
    try {
      const res = await api.post(`/rules/${r.id}/preview`, { index }, { responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(res.data));
    } catch (err) {
      setPdfError(await blobErrorMessage(err, 'Falha ao gerar preview'));
    } finally {
      setPdfLoading(false);
    }
  }

  const { data: rules } = useQuery({
    queryKey: ['rules'],
    queryFn: async () => (await api.get<Rule[]>('/rules')).data,
  });
  const { data: accounts } = useQuery({
    queryKey: ['gmail-accounts'],
    queryFn: async () => (await api.get<GmailAccount[]>('/gmail/accounts')).data,
  });
  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => (await api.get<DocumentTemplate[]>('/templates')).data,
  });

  const save = useMutation({
    mutationFn: async (d: Draft) => {
      const payload = {
        ...d,
        cron: d.trigger_type === 'schedule' ? specToCron(d.schedule_spec, d.cron) : null,
        schedule_spec: d.trigger_type === 'schedule' ? d.schedule_spec : null,
      };
      if (d.id) return (await api.put<Rule>(`/rules/${d.id}`, payload)).data;
      return (await api.post<Rule>('/rules', payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      setDraft(null);
      toast.success('Regra salva');
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao salvar')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/rules/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['rules'] }); toast.success('Regra removida'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao remover')),
  });

  const run = useMutation({
    mutationFn: async (id: string) => (await api.post<RuleRun>(`/rules/${id}/run`, {})).data,
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['rules'] });
      if (r.status === 'ok') toast.success(r.detail || 'Regra executada');
      else toast.error(`Erro: ${r.detail || 'falha desconhecida'}`);
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao rodar a regra')),
  });

  // Placeholders estáticos = união dos placeholders de todos os templates anexados.
  const tplById = (id: string) => templates?.find((t) => t.id === id);
  const staticPlaceholders = Array.from(
    new Set(
      (draft?.attachments ?? [])
        .flatMap((a) => tplById(a.template_id)?.placeholders ?? [])
        .filter((p) => !DYNAMIC.has(p)),
    ),
  );

  function addAttachment() {
    setDraft((d) => (d ? { ...d, attachments: [...d.attachments, { template_id: '', filename: '', as_pdf: false }] } : d));
  }
  function updateAttachment(i: number, patch: Partial<AttachmentDraft>) {
    setDraft((d) => (d ? { ...d, attachments: d.attachments.map((a, idx) => (idx === i ? { ...a, ...patch } : a)) } : d));
  }
  function removeAttachment(i: number) {
    setDraft((d) => (d ? { ...d, attachments: d.attachments.filter((_, idx) => idx !== i) } : d));
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    // Descarta anexos sem template selecionado.
    save.mutate({ ...draft, attachments: draft.attachments.filter((a) => a.template_id) });
  }

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Regras</h2>
        {!draft && (
          <button className="btn btn-primary text-sm" onClick={() => setDraft(emptyDraft())}>
            + Nova regra
          </button>
        )}
      </div>

      {(!accounts || accounts.length === 0) && (
        <div className="card text-sm text-amber-700 bg-amber-50">
          Conecte uma conta Gmail antes de criar regras de envio.
        </div>
      )}

      {draft && (
        <form onSubmit={handleSubmit} className="card space-y-4">
          <h3 className="font-semibold">{draft.id ? 'Editar regra' : 'Nova regra'}</h3>

          <div>
            <label className="block text-sm mb-1">Nome</label>
            <input className="input" value={draft.name} onChange={(e) => set('name', e.target.value)} required autoFocus />
          </div>

          {/* Gatilho */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-semibold px-1">Gatilho</legend>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={draft.trigger_type === 'schedule'}
                  onChange={() => set('trigger_type', 'schedule')}
                />
                Agendamento
              </label>
              <label className="flex items-center gap-1">
                <input
                  type="radio"
                  checked={draft.trigger_type === 'email_received'}
                  onChange={() => set('trigger_type', 'email_received')}
                />
                Email recebido
              </label>
            </div>

            {draft.trigger_type === 'schedule' ? (
              <CronBuilder
                spec={draft.schedule_spec}
                cron={draft.cron}
                onChange={(cron, spec) => setDraft((d) => (d ? { ...d, cron, schedule_spec: spec } : d))}
              />
            ) : (
              <div className="space-y-2 text-sm">
                <label className="block">
                  Conta observada
                  <select
                    className="input"
                    value={draft.watch_account_id}
                    onChange={(e) => set('watch_account_id', e.target.value)}
                    required
                  >
                    <option value="">— selecione —</option>
                    {accounts?.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.email}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  Remetente contém (opcional)
                  <input className="input" value={draft.match_from} onChange={(e) => set('match_from', e.target.value)} placeholder="boss@empresa.com" />
                </label>
                <label className="block">
                  Assunto contém (opcional)
                  <input className="input" value={draft.match_subject} onChange={(e) => set('match_subject', e.target.value)} placeholder="fatura" />
                </label>
              </div>
            )}
          </fieldset>

          {/* Ação: enviar email */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-semibold px-1">Ação · enviar email</legend>
            <div className="text-xs text-slate-500">
              Assunto, corpo e anexo aceitam placeholders — <PlaceholdersHelp />
            </div>
            <label className="block text-sm">
              Enviar de
              <select className="input" value={draft.from_account_id} onChange={(e) => set('from_account_id', e.target.value)} required>
                <option value="">— selecione —</option>
                {accounts?.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.email}
                  </option>
                ))}
              </select>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
              <label className="block">
                Para
                <input className="input" value={draft.to_addr} onChange={(e) => set('to_addr', e.target.value)} placeholder="dest@x.com" required />
              </label>
              <label className="block">
                Cc (opcional)
                <input className="input" value={draft.cc} onChange={(e) => set('cc', e.target.value)} />
              </label>
            </div>
            <label className="block text-sm">
              Assunto
              <input className="input" value={draft.subject_template} onChange={(e) => set('subject_template', e.target.value)} placeholder="Relatório {{ date }}" />
            </label>
            <label className="block text-sm">
              Corpo (aceita {'{{ placeholders }}'})
              <textarea className="input min-h-24" value={draft.body_template} onChange={(e) => set('body_template', e.target.value)} />
            </label>
          </fieldset>

          {/* Anexos */}
          <fieldset className="border rounded p-3 space-y-3">
            <legend className="text-sm font-semibold px-1">Anexos (opcional)</legend>

            {draft.attachments.length === 0 && (
              <p className="text-xs text-slate-500">Nenhum anexo. Adicione um ou mais documentos.</p>
            )}

            {draft.attachments.map((att, i) => {
              const tpl = tplById(att.template_id);
              return (
                <div key={i} className="border rounded p-2 space-y-2 bg-slate-50">
                  <div className="flex items-center gap-2">
                    <select
                      className="input flex-1"
                      value={att.template_id}
                      onChange={(e) => updateAttachment(i, { template_id: e.target.value })}
                    >
                      <option value="">— selecione um template —</option>
                      {templates?.map((t) => (
                        <option key={t.id} value={t.id}>{t.name} ({t.file_type})</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="text-xs text-red-600 hover:text-red-800 shrink-0 px-1"
                      onClick={() => removeAttachment(i)}
                    >
                      remover
                    </button>
                  </div>
                  {tpl && (
                    <>
                      <input
                        className="input text-sm"
                        value={att.filename}
                        onChange={(e) => updateAttachment(i, { filename: e.target.value })}
                        placeholder={`nome-{{ mes_ano }}.${att.as_pdf && tpl.file_type !== 'pdf' ? 'pdf' : tpl.file_type}`}
                      />
                      {tpl.file_type !== 'pdf' && (
                        <label className="flex items-center gap-2 text-sm">
                          <input
                            type="checkbox"
                            checked={att.as_pdf}
                            onChange={(e) => updateAttachment(i, { as_pdf: e.target.checked })}
                          />
                          Converter para PDF ao gerar/enviar
                        </label>
                      )}
                    </>
                  )}
                </div>
              );
            })}

            <button type="button" className="btn btn-secondary text-xs" onClick={addAttachment}>
              + Adicionar anexo
            </button>

            {staticPlaceholders.length > 0 && (
              <div className="text-sm space-y-2 border-t pt-2">
                <div className="text-xs text-slate-500">
                  Valores fixos dos placeholders (deixe vazio os dinâmicos como date/email_*):
                </div>
                {staticPlaceholders.map((p) => (
                  <label key={p} className="block">
                    <code className="text-xs">{p}</code>
                    <input
                      className="input"
                      value={draft.template_context[p] ?? ''}
                      onChange={(e) =>
                        set('template_context', { ...draft.template_context, [p]: e.target.value })
                      }
                    />
                  </label>
                ))}
              </div>
            )}
          </fieldset>

          <fieldset className="border rounded p-3 space-y-2">
            <legend className="text-sm font-semibold px-1">Notificações (push)</legend>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.notify_on_error} onChange={(e) => set('notify_on_error', e.target.checked)} />
              Notificar quando falhar
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.notify_on_success} onChange={(e) => set('notify_on_success', e.target.checked)} />
              Notificar quando executar com sucesso
            </label>
            {draft.trigger_type === 'email_received' && (
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={draft.notify_on_match} onChange={(e) => set('notify_on_match', e.target.checked)} />
                Notificar quando um email casar com o filtro
              </label>
            )}
            <p className="text-xs text-slate-500">
              Requer push ativado em <strong>Notificações</strong> neste dispositivo.
            </p>
          </fieldset>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={draft.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Ativa
          </label>

          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={save.isPending}>
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setDraft(null)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      {rules?.length === 0 && !draft && (
        <div className="card text-sm text-slate-600 text-center">Nenhuma regra ainda.</div>
      )}

      <div className="space-y-2">
        {rules?.map((r) => (
          <div key={r.id} className="card space-y-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium flex items-center gap-2 flex-wrap">
                  {r.name}
                  {!r.enabled && <span className="text-xs bg-slate-200 rounded px-1">pausada</span>}
                  {r.last_status === 'ok' && <span className="text-xs text-emerald-600">● ok</span>}
                  {r.last_status === 'error' && <span className="text-xs text-red-600">● erro</span>}
                </div>
                <div className="text-xs text-slate-500">
                  {r.trigger_type === 'schedule' ? (
                    <>agendada · <code>{r.cron}</code></>
                  ) : (
                    <>email recebido → {r.match_from || 'qualquer'} / {r.match_subject || 'qualquer assunto'}</>
                  )}{' '}
                  · envia p/ {r.to_addr}
                  {r.last_run_at && <> · último: {new Date(r.last_run_at).toLocaleString('pt-BR')}</>}
                </div>
                {r.last_error && <div className="text-xs text-red-600 break-words">{r.last_error}</div>}
              </div>
              <div className="flex gap-2 shrink-0 flex-wrap">
                {(r.attachments ?? []).map((_, i) => (
                  <button
                    key={i}
                    className="btn btn-secondary text-xs py-1"
                    onClick={() => openPreview(r, i)}
                    disabled={pdfLoading}
                  >
                    {(r.attachments?.length ?? 0) > 1 ? `Preview ${i + 1}` : 'Preview anexo'}
                  </button>
                ))}
                <button className="btn btn-secondary text-xs py-1" onClick={() => run.mutate(r.id)} disabled={run.isPending}>
                  {run.isPending && run.variables === r.id ? 'Rodando…' : 'Rodar agora'}
                </button>
                <button className="btn btn-secondary text-xs py-1" onClick={() => setDraft(ruleToDraft(r))}>
                  Editar
                </button>
                <button
                  className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed px-2"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm(`Remover regra ${r.name}?`)) remove.mutate(r.id);
                  }}
                >
                  Remover
                </button>
              </div>
            </div>
            <button
              className="text-xs text-brand-600 hover:underline"
              onClick={() => setRunsFor(runsFor === r.id ? null : r.id)}
            >
              {runsFor === r.id ? 'Ocultar histórico' : 'Ver histórico'}
            </button>
            {runsFor === r.id && <RunsList ruleId={r.id} />}
          </div>
        ))}
      </div>

      {previewRule && (
        <PdfPreviewModal
          title={`Anexo · ${previewRule.name}`}
          url={pdfUrl}
          loading={pdfLoading}
          error={pdfError}
          onClose={closePreview}
        />
      )}
    </div>
  );
}

function RunsList({ ruleId }: { ruleId: string }) {
  const { data: runs } = useQuery({
    queryKey: ['rule-runs', ruleId],
    queryFn: async () => (await api.get<RuleRun[]>(`/rules/${ruleId}/runs`)).data,
  });
  if (!runs?.length) return <div className="text-xs text-slate-500">Sem execuções ainda.</div>;
  return (
    <ul className="text-xs space-y-1 border-t pt-2">
      {runs.map((run) => (
        <li key={run.id} className="flex gap-2">
          <span className={run.status === 'ok' ? 'text-emerald-600' : 'text-red-600'}>●</span>
          <span className="text-slate-500">{new Date(run.started_at).toLocaleString('pt-BR')}</span>
          <span className="text-slate-700 truncate">{run.detail}</span>
        </li>
      ))}
    </ul>
  );
}
